import { useState, type FormEvent } from 'react';
import { Button, Card, CardBody, ErrorNote, Field, InfoNote, PageHeader, inputClass } from '../components/ui';
import { ApiError, describeApiError } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useSession } from '../lib/session';

/**
 * Your own account: who you are, your display name, and your password.
 *
 * Reachable by every signed-in user from the header menu — there is no section
 * for it and no capability gates it, because the only record it can touch is the
 * caller's own. When an administrator has issued a temporary password the API
 * refuses every other screen and the app lands here until it is replaced.
 */
function NameForm() {
  const { user, updateProfile, isUpdatingProfile } = useSession();
  const [name, setName] = useState(user?.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await updateProfile(name.trim());
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? describeApiError(caught) : 'Could not save your name.');
    }
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Field label="Display name">
        <input
          className={inputClass}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setSaved(false);
          }}
          required
        />
      </Field>

      <ErrorNote>{error ?? ''}</ErrorNote>
      {saved && !error ? (
        <p className="mt-2 text-sm text-brand-deep">Saved.</p>
      ) : null}

      <div className="mt-3">
        <Button type="submit" disabled={isUpdatingProfile || name.trim() === user?.name}>
          {isUpdatingProfile ? 'Saving…' : 'Save name'}
        </Button>
      </div>
    </form>
  );
}

function PasswordForm() {
  const { changePassword, isChangingPassword } = useSession();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('The new password and confirmation do not match.');
      return;
    }

    try {
      // The session is gone the moment this resolves, so the protected layout
      // sends the person to sign-in and the session context carries the notice.
      await changePassword(currentPassword, newPassword);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? describeApiError(caught) : 'Could not change your password.',
      );
    }
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Current password">
          <input
            className={inputClass}
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
          />
        </Field>
        <span className="hidden sm:block" />
        <Field label="New password" hint="At least 12 characters">
          <input
            className={inputClass}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
            minLength={12}
          />
        </Field>
        <Field label="Confirm new password">
          <input
            className={inputClass}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
            minLength={12}
          />
        </Field>
      </div>

      <ErrorNote>{error ?? (mismatch ? 'The new password and confirmation do not match.' : '')}</ErrorNote>

      <p className="mt-3 text-xs text-ink-muted">
        Changing your password signs you out of every device, including this one. You will sign in
        again with the new password.
      </p>

      <div className="mt-3">
        <Button type="submit" disabled={isChangingPassword || mismatch}>
          {isChangingPassword ? 'Changing…' : 'Change password'}
        </Button>
      </div>
    </form>
  );
}

export function ProfilePage() {
  const { user } = useSession();

  if (!user) return null;

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Your profile"
        description="Who you are signed in as, your display name, and your password."
      />

      {user.mustChangePassword ? (
        <div className="mb-6">
          <InfoNote>
            An administrator has issued you a temporary password. Choose a new one below before
            continuing — every other screen stays locked until you do.
          </InfoNote>
        </div>
      ) : null}

      <Card className="mb-6">
        <CardBody>
          <h2 className="text-base font-bold text-ink">Identity</h2>
          <dl className="mt-3 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="w-32 shrink-0 text-ink-muted">Name</dt>
              <dd className="font-medium text-ink">{user.name}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-32 shrink-0 text-ink-muted">Email</dt>
              <dd className="font-mono text-xs text-ink-body">{user.email}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-32 shrink-0 text-ink-muted">Roles</dt>
              <dd className="text-ink-body">
                {user.roles.map((role) => role.name).join(', ') || 'No role'}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-32 shrink-0 text-ink-muted">Password changed</dt>
              <dd className="text-ink-body">{formatDateTime(user.passwordChangedAt)}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      <Card className="mb-6">
        <CardBody>
          <h2 className="text-base font-bold text-ink">Display name</h2>
          <p className="mt-1 text-sm text-ink-body">
            Shown in the header and beside anything you record.
          </p>
          <div className="mt-4 max-w-md">
            <NameForm />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="text-base font-bold text-ink">Password</h2>
          <p className="mt-1 text-sm text-ink-body">
            Changing your password signs out every session, including any other device you are
            signed in on.
          </p>
          <div className="mt-4 max-w-2xl">
            <PasswordForm />
          </div>
        </CardBody>
      </Card>
    </>
  );
}
